import Mention from "@tiptap/extension-mention";
import { ReactRenderer } from "@tiptap/react";
import { api } from "../api/client";
import { MentionList, type MentionListHandle } from "./MentionList";
import type { MentionHit, MentionList as MentionListDto, MentionType } from "../api/types";

// 內文裡的 `@` 提及(第 22 輪)。
//
// 業主 2026-09-19:「我要的是直接可以在內文提及 客戶打 @之後」。
//
// **存成什麼**:`<span data-mention-type="QUOTE" data-mention-id="12">@…</span>`
// 那兩個 `data-*` 是後端 `app/sanitize.py` 唯一放行的 —— 改這裡的屬性名,
// 那邊要一起改,不然存檔時提及會被洗掉。
//
// **為什麼不讓人自己打**:打錯了不會報錯。把 `@林品豪` 打成 `@林品毫`,
// 那一行只會安靜地變成純文字,而寫的人以為自己連上了。所以提及只能從清單
// 挑出來 —— 挑出來的是一顆看得見的膠囊,自己打的永遠是純文字。

/** 類別篩選是跨次呼叫的狀態,所以放在模組層。 */
let filter: MentionType | "" = "";

async function fetchHits(query: string): Promise<MentionHit[]> {
  try {
    const got = await api.get<MentionListDto>("/mentions", {
      q: query,
      ...(filter ? { type: filter } : {}),
    });
    return got.items ?? [];
  } catch {
    // 撈不到就給空的 —— 清單自己會說「沒有符合的」。
    return [];
  }
}

export function createMention() {
  return Mention.configure({
    HTMLAttributes: { class: "nq-mention" },
    // 存進 HTML 的形狀。**與後端的白名單對齊。**
    renderHTML({ node }) {
      return [
        "span",
        {
          class: "nq-mention",
          "data-mention-type": String(node.attrs.type ?? ""),
          "data-mention-id": String(node.attrs.id ?? ""),
        },
        `@${node.attrs.label ?? ""}`,
      ];
    },
    renderText({ node }) {
      return `@${node.attrs.label ?? ""}`;
    },
    suggestion: {
      char: "@",
      // **中文輸入法組字中不要觸發** —— 組字過程會讓浮層一直閃。
      allowSpaces: false,
      items: ({ query }: { query: string }) => fetchHits(query),
      // TipTap 的型別把 props 宣告成 `MentionNodeAttrs`(只有 id / label),
      // 而我們多帶一個 `type`。用 `any` 收下再自己轉回來 ——
      // 那三個欄位由 `addAttributes` 定義,是這個擴充自己的約定。
      command: ({ editor, range, props }: any) => {
        const hit = props as MentionHit;
        editor
          .chain()
          .focus()
          .insertContentAt(range, [
            {
              type: "mention",
              attrs: { id: hit.id, type: hit.type, label: hit.label },
            },
            { type: "text", text: " " },
          ])
          .run();
      },
      render: () => {
        let component: ReactRenderer<MentionListHandle> | null = null;
        let box: HTMLDivElement | null = null;

        /** 把浮層放在游標旁邊。**不用 popper** —— 一個相依換十行算式。 */
        const place = (rect: DOMRect | null) => {
          if (!box || !rect) return;
          const gap = 6;
          const below = window.innerHeight - rect.bottom;
          box.style.left = `${Math.min(rect.left, window.innerWidth - 340)}px`;
          // 下面空間不夠就翻到上面 —— 在螢幕底下打字是常態。
          if (below < 260) {
            box.style.top = "";
            box.style.bottom = `${window.innerHeight - rect.top + gap}px`;
          } else {
            box.style.bottom = "";
            box.style.top = `${rect.bottom + gap}px`;
          }
        };

        return {
          onStart: (props: any) => {
            component = new ReactRenderer(MentionList, {
              editor: props.editor,
              props: {
                items: props.items ?? [],
                filter,
                onFilter: (next: MentionType | "") => {
                  filter = next;
                  // 換了篩選要重新撈一次 —— 不重撈的話按鈕看起來沒反應。
                  void fetchHits(props.query ?? "").then((items) => {
                    component?.updateProps({ items, filter });
                  });
                },
                command: props.command,
              },
            });
            box = document.createElement("div");
            box.style.position = "fixed";
            box.style.zIndex = "60";
            box.appendChild(component.element);
            document.body.appendChild(box);
            place(props.clientRect?.() ?? null);
          },
          onUpdate: (props: any) => {
            component?.updateProps({
              items: props.items ?? [],
              filter,
              command: props.command,
            });
            place(props.clientRect?.() ?? null);
          },
          onKeyDown: (props: { event: KeyboardEvent }) => {
            if (props.event.key === "Escape") {
              box?.remove();
              return true;
            }
            return component?.ref?.onKeyDown(props.event) ?? false;
          },
          onExit: () => {
            box?.remove();
            box = null;
            component?.destroy();
            component = null;
            // 下一次打 `@` 從「全部」開始 —— 上一次的篩選留著會讓人找不到東西。
            filter = "";
          },
        };
      },
    },
  }).extend({
    addAttributes() {
      return {
        id: { default: null },
        type: { default: null },
        label: { default: null },
      };
    },
  });
}
